import { supabase } from '@/lib/supabase';
import { OrderSubmission, SubmissionResponse } from '@/types/order.types';
import { CatalogOrderData, CatalogOrderResponse, Product } from '@/types/order.types';

export const orderService = {
  async submitOrder(orderData: OrderSubmission): Promise<SubmissionResponse> {
    try {
      // 1. Save to database
      const { data: dbData, error: dbError } = await supabase
        .from('order_submissions')
        .insert([{
          full_name: orderData.fullName,
          email: orderData.email,
          phone: orderData.phone,
          company: orderData.company || null,
          project_name: orderData.projectName,
          project_type: orderData.projectType,
          location: orderData.location,
          building_floors: orderData.buildingFloors,
          product_type: orderData.productType,
          quantity: orderData.quantity,
          capacity: orderData.capacity || null,
          speed: orderData.speed || null,
          installation_timeframe: orderData.installationTimeframe || null,
          budget: orderData.budget || null,
          additional_notes: orderData.additionalNotes || null
        }])
        .select()
        .single();

      if (dbError) throw dbError;

      // 2. Send email via Edge Function
      const { data: emailData, error: emailError } = await supabase.functions.invoke(
        'send-order-email',
        {
          body: orderData
        }
      );

      if (emailError) {
        console.error('Email sending failed:', emailError);
        // Don't throw - order is saved, email failure shouldn't block user
      }

      return {
        success: true,
        message: 'Order submitted successfully! We will contact you within 24 hours.'
      };
    } catch (error: any) {
      console.error('Order submission error:', error);
      return {
        success: false,
        error: error?.message || 'Failed to submit order. Please try again.'
      };
    }
  },

  async getAllSubmissions(): Promise<{ data: OrderSubmission[] | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('order_submissions')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;

      return {
        data: data?.map(item => ({
          id: item.id,
          fullName: item.full_name,
          email: item.email,
          phone: item.phone,
          company: item.company,
          projectName: item.project_name,
          projectType: item.project_type,
          location: item.location,
          buildingFloors: item.building_floors,
          productType: item.product_type,
          quantity: item.quantity,
          capacity: item.capacity,
          speed: item.speed,
          installationTimeframe: item.installation_timeframe,
          budget: item.budget,
          additionalNotes: item.additional_notes,
          createdAt: item.created_at,
          updatedAt: item.updated_at
        })) || null,
        error: null
      };
    } catch (error) {
      return { data: null, error };
    }
  },

  async getAllProducts(): Promise<{ data: Product[] | null; error: any }> {
    try {
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('is_active', true);

      if (error) throw error;

      return {
        data: data?.map(item => ({
          id: item.id,
          part_id: item.part_id,
          description: item.description,
          price: parseFloat(item.price),
          category: item.category,
          is_active: item.is_active
        })) || null,
        error: null
      };
    } catch (error) {
      console.error('Error fetching products:', error);
      return { data: null, error };
    }
  },

  async submitCatalogOrder(orderData: CatalogOrderData): Promise<CatalogOrderResponse> {
    try {
      // 1. Create the order record
      const { data: orderRecord, error: orderError } = await supabase
        .from('catalog_orders')
        .insert([{
          company_name: orderData.company_name,
          contact_name: orderData.contact_name,
          email: orderData.email,
          phone: orderData.phone,
          order_total: orderData.order_total,
          notes: orderData.notes || null
        }])
        .select()
        .single();

      if (orderError) throw orderError;

      // 2. Create order items
      const orderItems = orderData.items.map(item => ({
        order_id: orderRecord.id,
        product_id: item.product_id,
        part_id: item.part_id,
        description: item.description,
        quantity: item.quantity,
        unit_price: item.unit_price,
        line_total: item.line_total
      }));

      const { error: itemsError } = await supabase
        .from('order_items')
        .insert(orderItems);

      if (itemsError) throw itemsError;

      // 3. Send email notification via Edge Function
      const { error: emailError } = await supabase.functions.invoke(
        'send-order-email',
        {
          body: {
            type: 'catalog_order',
            orderId: orderRecord.id,
            ...orderData
          }
        }
      );

      if (emailError) {
        console.error('Email sending failed:', emailError);
        // Don't throw - order is saved, email failure shouldn't block user
      }

      return {
        success: true,
        orderId: orderRecord.id,
        message: 'Order submitted successfully! We will contact you within 24 hours.'
      };
    } catch (error: any) {
      console.error('Catalog order submission error:', error);
      return {
        success: false,
        error: error?.message || 'Failed to submit order. Please try again.'
      };
    }
  }
};